import { EventTarget, warn } from 'cc';
import Game from '../../Game';
import Delay from '../Utils/Delay';
import ErrorName from '../../ErrorHandler/ErrorName';

enum QueryEventType {
    DATA_LIST_UPDATED = 'DATA_LIST_UPDATED',
    IS_REQUESTING_STATE_CHANGED = 'IS_REQUESTING_DATA_LIST_STATE_CHANGED',
}

enum SyncState {
    NotSync,
    Syncing,
    Synced,
}

export enum SortOrder {
    Ascending,
    Descending,
}

type QueryConfig<TData extends {}> = {
    query: (page: number, pageSize: number) => Promise<TData[] | [TData[], boolean]>;
    getDataIndex: (data: TData) => number;
    isSameData?: (data1: TData, data2: TData) => boolean;
    sortOrder?: SortOrder;
    chunkSize?: number;
    requestDataInterval?: number;
    tooManyRequestsRetryDelay?: number;
};

class QueryDataChunk<TData extends {}> {
    private readonly _dataList: TData[] = [];
    private _startIndex: number = -1;
    private _endIndex: number = -1;

    constructor(
        dataList: TData[],
        private readonly config: QueryConfig<TData>,
        public noMoreData: boolean,
    ) {
        this._startIndex = this.config.getDataIndex(dataList.find((data) => !!data));
        this._endIndex = this.config.getDataIndex(dataList[dataList.length - 1]);
        this._dataList = dataList;
    }

    public get dataList() {
        return [...this._dataList];
    }

    public get startIndex() {
        return this._startIndex;
    }

    public get endIndex() {
        return this._endIndex;
    }

    public merge(targetDataChunk: this): void {
        // determine which chunk is newer and which is older
        const newerChunk = this._compareDataIndex(this._startIndex, targetDataChunk.startIndex) < 0 ? this : targetDataChunk;
        const olderChunk = newerChunk === targetDataChunk ? this : targetDataChunk;

        // find the overlapped data
        const overlappedData = olderChunk._dataList.find((data) => !!data);
        const overlappedDataIndex = newerChunk._dataList.findIndex(
            (data) => data && this.config.getDataIndex(data) === this.config.getDataIndex(overlappedData),
        );
        if (overlappedDataIndex === -1) {
            throw new Error('Overlapped data chunk must have overlapped data');
        }

        // merge two data chunks
        this._startIndex = newerChunk._startIndex;
        // choose the larger end index between two chunks
        this._endIndex = this._compareDataIndex(this._endIndex, targetDataChunk.endIndex) < 0 ? targetDataChunk._endIndex : this.endIndex;
        const newDataList = [
            ...newerChunk._dataList.slice(0, overlappedDataIndex),
            ...olderChunk._dataList.filter((data) => !!data),
            ...newerChunk._dataList.slice(overlappedDataIndex + olderChunk._dataList.length),
        ];
        this._dataList.length = 0;
        this._dataList.push(...newDataList);
        this.noMoreData = olderChunk.noMoreData;
    }

    public append(newDataChunk: this): void {
        if (!this.newerThan(newDataChunk)) {
            throw new Error("Can't append data chunk which is newer than self");
        }
        if (this.overlapWith(newDataChunk)) {
            throw new Error('Only non-overlapped data chunks could be appended');
        }
        this._dataList.push(...newDataChunk._dataList);
        this._endIndex = newDataChunk.endIndex;
        this.noMoreData = newDataChunk.noMoreData;
    }

    public newerThan(dataChunk: this): boolean {
        return this._compareDataIndex(this._startIndex, dataChunk.startIndex) < 0;
    }

    public overlapWith(dataChunk: this): boolean {
        if (this._compareDataIndex(this._endIndex, dataChunk.startIndex) < 0) {
            return false;
        }
        if (this._compareDataIndex(this._startIndex, dataChunk.endIndex) > 0) {
            return false;
        }
        return true;
    }

    private _compareDataIndex(index1: number, index2: number): number {
        return this.config.sortOrder === SortOrder.Descending ? index2 - index1 : index1 - index2;
    }
}

class QueryDataList<TData extends {}> {
    private _staleDataChunkList: QueryDataChunk<TData>[] = [];
    private _currentDataChunk: QueryDataChunk<TData> = null;

    constructor(private readonly config: QueryConfig<TData>) {}

    public get currentDataChunk(): QueryDataChunk<TData> {
        return this._currentDataChunk;
    }

    public get dataList() {
        return this._currentDataChunk ? [...this._currentDataChunk.dataList] : [];
    }

    public get nextChunkIndex() {
        return this._currentDataChunk ? Math.ceil((this._currentDataChunk.dataList.length + 1) / this.config.chunkSize) : 1;
    }

    public get noMoreData() {
        return this._currentDataChunk ? this._currentDataChunk.noMoreData : false;
    }

    private get isCurrentChunkFull() {
        if (!this._currentDataChunk || !this._currentDataChunk.dataList.length) {
            return false;
        }
        return this._currentDataChunk.dataList.length % this.config.chunkSize === 0;
    }

    public staleCurrentDataChunk() {
        if (!this._currentDataChunk) {
            return;
        }
        this._staleDataChunkList.unshift(this._currentDataChunk);
        this._currentDataChunk = null;
    }

    /**
     * @param dataList data to be added
     * @returns true if the data list is updated
     */
    public addData(dataList: TData[], noMoreData: boolean = dataList.length < this.config.chunkSize): boolean {
        if (!dataList.length) {
            if (this._currentDataChunk) {
                this._currentDataChunk.noMoreData = true;
            }
            return false;
        }

        const newDataChunk = new QueryDataChunk(dataList, this.config, noMoreData);

        // make sure current data chunk is the newer one
        if (this._currentDataChunk) {
            if (this._currentDataChunk.newerThan(newDataChunk)) {
                // if current data chunk is full, new data chunk can be appended to it directly, because there is no data between them
                if (this.isCurrentChunkFull && !this._currentDataChunk.overlapWith(newDataChunk)) {
                    this._currentDataChunk.append(newDataChunk);
                } else {
                    this._staleDataChunkList.unshift(newDataChunk);
                }
            } else {
                this._staleDataChunkList.unshift(this._currentDataChunk);
                this._currentDataChunk = newDataChunk;
            }
        } else {
            this._currentDataChunk = newDataChunk;
        }

        // try to merge with stale data chunk
        const staleDataChunkList = [...this._staleDataChunkList];
        for (let i = 0; i < staleDataChunkList.length; i++) {
            const staleDataChunk = staleDataChunkList[i];
            if (this._currentDataChunk.overlapWith(staleDataChunk)) {
                this._currentDataChunk.merge(staleDataChunk);
                const index = this._staleDataChunkList.indexOf(staleDataChunk);
                if (index !== -1) {
                    this._staleDataChunkList.splice(index, 1);
                }
            }
        }
        return true;
    }

    public resetNoMoreData() {
        if (!this._currentDataChunk) {
            return;
        }
        this._currentDataChunk.noMoreData = false;
    }
}

export default class DataQuery<TData extends {}> extends EventTarget {
    public static readonly EVENT_TYPE = QueryEventType;
    public static readonly DEFAULT_CHUNK_SIZE = 30;
    public static readonly DEFAULT_REQUEST_DATA_INTERVAL = 500;
    public static readonly TOO_MANY_REQUESTS_RETRY_DELAY = 3000;

    private _queryDataList: QueryDataList<TData> = null;
    private _syncState: SyncState = SyncState.NotSync;
    private _isRequesting = false;
    private _checkNewDataFlag: number = 0;

    constructor(private readonly config: QueryConfig<TData>) {
        super();
        config.isSameData ??= (data1: TData, data2: TData) => config.getDataIndex(data1) === config.getDataIndex(data2);
        config.sortOrder ??= SortOrder.Ascending;
        config.chunkSize ??= DataQuery.DEFAULT_CHUNK_SIZE;
        config.requestDataInterval ??= DataQuery.DEFAULT_REQUEST_DATA_INTERVAL;
        config.tooManyRequestsRetryDelay ??= DataQuery.TOO_MANY_REQUESTS_RETRY_DELAY;
    }

    public get isRequesting() {
        return this._isRequesting;
    }
    private set isRequesting(value: boolean) {
        if (value === this._isRequesting) {
            return;
        }
        this._isRequesting = value;
        this.emit(QueryEventType.IS_REQUESTING_STATE_CHANGED);
    }

    public get isSynced() {
        return this._syncState === SyncState.Synced;
    }

    public get dataList() {
        return this.queryDataList.dataList;
    }

    public get noMoreData() {
        return this.queryDataList.noMoreData;
    }

    private get queryDataList(): QueryDataList<TData> {
        if (!this._queryDataList) {
            this._queryDataList = new QueryDataList(this.config);
        }
        return this._queryDataList;
    }

    public async checkSyncState() {
        if (this._syncState === SyncState.NotSync) {
            this._syncState = SyncState.Syncing;
            const hasNewData = await this._checkNewData();
            this._syncState = SyncState.Synced;
            if (hasNewData) {
                // if there is new data, stale current data chunk and request for new data
                this.queryDataList.staleCurrentDataChunk();
                await this.requestForNewData();
            }
        } else if (this._syncState === SyncState.Syncing) {
            await Delay.forever();
        }
    }

    public resetSyncState() {
        this._syncState = SyncState.NotSync;
    }

    public resetNoMoreData() {
        this.queryDataList.resetNoMoreData();
    }

    public async requestForNewData(targetNewDataCount: number = this.config.chunkSize) {
        if (!this.isSynced) {
            warn('DataQuery has not been synced, request for new data is ignored');
            return;
        }
        const initialCount = this.queryDataList.dataList.length;
        let newDataCount = 0;
        let times = 0;
        while (!this.queryDataList.noMoreData && newDataCount < targetNewDataCount) {
            // add delay to prevent too frequent request
            await Promise.all([this._requestForNextDataChunk(), Delay.resolve(this.config.requestDataInterval * times++)]);
            newDataCount = this.queryDataList.dataList.length - initialCount;
            // if the new data count is less than chunk size, it means no more data
            if (newDataCount < this.config.chunkSize) {
                break;
            }
        }
    }

    private async _requestForNextDataChunk() {
        const [newDataList, noMoreData] = await this._query(this.queryDataList.nextChunkIndex, this.config.chunkSize);
        const hasNewData = this.queryDataList.addData(newDataList, noMoreData);
        if (hasNewData) {
            this.emit(QueryEventType.DATA_LIST_UPDATED);
        }
    }

    /**
     * @returns true if need to refresh data
     */
    private async _checkNewData() {
        if (!this.queryDataList.currentDataChunk) {
            return true;
        }
        this._checkNewDataFlag++;
        const [currentFirstData] = this.queryDataList.dataList;
        const [[newFirstData], noMoreData] = await this._query(1, 1, true);
        if (typeof noMoreData === 'boolean' && !noMoreData) {
            return true;
        }
        if (!newFirstData) {
            return false;
        }
        return !this.config.isSameData(currentFirstData, newFirstData);
    }

    private async _query(page: number, pageSize: number, force = false, handleBusy: boolean = true): Promise<[TData[], boolean?]> {
        if (this.isRequesting && !force) {
            return Delay.forever();
        }
        this.isRequesting = true;
        try {
            const response = await this.config.query(page, pageSize);
            this.isRequesting = false;
            if (!Array.isArray(response[0])) {
                return [response as TData[]];
            }
            return response as [TData[], boolean];
        } catch (error) {
            if ((error !== ErrorName.SYSTEM_BUSY && error !== ErrorName.SYSTEM_BUSY2) || handleBusy) {
                Game.error.handle(error);
            }
            if (error === ErrorName.SYSTEM_BUSY || error === ErrorName.SYSTEM_BUSY2) {
                const flag = this._checkNewDataFlag;
                await Delay.resolve(this.config.tooManyRequestsRetryDelay);
                // retry if the flag is not changed, which means no new data check is performed during the delay
                if (flag === this._checkNewDataFlag) {
                    return this._query(page, pageSize, true, false);
                }
            } else {
                this.isRequesting = false;
            }
        }
        return Delay.forever();
    }
}
